import { cn } from "@/lib/utils";

interface NetflixBookSkeletonProps {
  className?: string;
  /** Show title and tags below card */
  showMeta?: boolean;
  /** Aspect ratio variant */
  variant?: 'portrait' | 'landscape';
}

/**
 * Netflix-style shimmer skeleton for book cards
 * Uses a gradient animation that sweeps across the card
 */
export function NetflixBookSkeleton({ 
  className,
  showMeta = true,
  variant = 'portrait'
}: NetflixBookSkeletonProps) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* Cover shimmer */}
      <div 
        className={cn(
          "relative overflow-hidden rounded-2xl bg-white/5",
          variant === 'portrait' 
            ? "aspect-[2/3]" 
            : "aspect-video"
        )}
      >
        {/* Shimmer effect */}
        <div 
          className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
          }}
        />
      </div>
      
      {/* Meta shimmer */}
      {showMeta && (
        <div className="space-y-2 px-0.5">
          <div className="h-4 w-3/4 rounded bg-white/5 overflow-hidden relative">
            <div 
              className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
              }}
            />
          </div>
          <div className="flex gap-2">
            <div className="h-3 w-12 rounded-full bg-white/5 overflow-hidden relative">
              <div 
                className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
                }}
              />
            </div>
            <div className="h-3 w-10 rounded-full bg-white/5 overflow-hidden relative">
              <div 
                className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Netflix-style shimmer skeleton for cinema carousel cards
 */
export function NetflixCinemaCardSkeleton({ className }: { className?: string }) {
  return (
    <div 
      className={cn(
        "relative overflow-hidden rounded-3xl bg-white/5 aspect-video",
        className
      )}
    >
      {/* Shimmer effect */}
      <div 
        className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
        }}
      />
      
      {/* Bottom gradient for text area */}
      <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" />
      
      {/* Fake badge */}
      <div className="absolute top-3 left-3">
        <div className="h-5 w-16 rounded-lg bg-white/10 overflow-hidden relative">
          <div 
            className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
            }}
          />
        </div>
      </div>
      
      {/* Fake title */}
      <div className="absolute bottom-4 left-4 right-4 space-y-2">
        <div className="h-3 w-12 rounded bg-white/10 overflow-hidden relative">
          <div 
            className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
            }}
          />
        </div>
        <div className="h-5 w-2/3 rounded bg-white/10 overflow-hidden relative">
          <div 
            className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
            }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Netflix-style row skeleton with header
 */
export function NetflixRowSkeleton({ 
  cardCount = 6,
  showTitle = true,
  variant = 'portrait'
}: { 
  cardCount?: number;
  showTitle?: boolean;
  variant?: 'portrait' | 'landscape';
}) {
  return (
    <div className="space-y-3">
      {showTitle && (
        <div className="px-4 md:px-12 xl:px-16">
          <div className="h-6 w-40 rounded bg-white/5 overflow-hidden relative">
            <div 
              className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
              }}
            />
          </div>
        </div>
      )}
      <div className="flex gap-3 px-4 md:px-12 xl:px-16 overflow-hidden">
        {Array.from({ length: cardCount }).map((_, i) => (
          <div 
            key={i} 
            className={cn(
              "flex-shrink-0",
              variant === 'portrait' 
                ? "w-[140px] md:w-[200px] xl:w-[220px]"
                : "w-[208px] sm:w-[260px] md:w-[312px]"
            )}
            style={{ 
              animationDelay: `${i * 100}ms` 
            }}
          >
            {variant === 'landscape' ? (
              <NetflixCinemaCardSkeleton />
            ) : (
              <NetflixBookSkeleton showMeta />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
